import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PencilSimpleIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { apiFetch } from '../../../auth/apiFetch';
import { pollWithWake } from '../../../data/kioskRefresh';
import { Modal, ModalFooter } from '../../../components/Modal';
import { useScrollFade } from '../useScrollFade';
import { OnScreenKeyboard } from '../OnScreenKeyboard';

// Fixed bottom-of-screen overlay that floats the on-screen keyboard above the
// kiosk grid (the widgets are small, overflow-hidden cards, so the keyboard
// can't dock in-flow like it does in the fullscreen music modal). z-[60] keeps
// it above the rename Modal (z-50). Portaled to <body> so no card clips it.
function KeyboardOverlay(props: React.ComponentProps<typeof OnScreenKeyboard>) {
  return createPortal(
    <div className="fixed inset-x-0 bottom-0 z-[60] bg-canvas/95 backdrop-blur-sm px-6">
      <OnScreenKeyboard {...props} />
    </div>,
    document.body,
  );
}

// Kitchen kiosk shopping-list widget backed by the AnyList HA integration.
// Lists (Haggen + Costco + Amazon + Todo) shown as a tab
// strip; each is a scrollable column of items. Interactions:
//   - Tap "+" or hit Enter in the input → add an item to the active list
//   - Tap an item's checkbox → mark complete (anylist.check_item) → vanishes
//     (we hide completed items by definition, so checking == removing)
//   - Tap the pencil → edit modal with the name input, Save (rename via
//     remove + re-add), and Delete (anylist.remove_item, a real delete)
//
// Optimistic UI: every mutation updates local state immediately and then
// fires the corresponding HA service call. The next poll reconciles —
// pending items disappear when the server snapshot agrees, or snap back
// if the call failed.
//
// Persistence: activeList is stored in localStorage so the kiosk reopens
// to whichever tab the user was last viewing.

// Names must match the AnyList app exactly — it's "Haggen" (the store chain's
// spelling), verified against the live integration. "To Sell" still exists in
// AnyList; it's just not surfaced on the kiosk.
const LISTS = ['Haggen', 'Costco', 'Amazon', 'Todo'] as const;
type ListName = (typeof LISTS)[number];

const ACTIVE_LIST_STORAGE_KEY = 'kiosk:anylist:activeList';
const POLL_INTERVAL_MS = 8_000;

interface GetItemsResponse {
  ok?: boolean;
  response?: {
    service_response?: {
      code?: number;
      items?: string[];
    };
  };
}

function readPersistedList(): ListName {
  if (typeof window === 'undefined') return LISTS[0];
  try {
    const stored = window.localStorage.getItem(ACTIVE_LIST_STORAGE_KEY);
    if (stored && (LISTS as readonly string[]).includes(stored)) {
      return stored as ListName;
    }
  } catch {
    // localStorage can throw in private-browsing modes — fall through.
  }
  return LISTS[0];
}

function writePersistedList(list: ListName): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ACTIVE_LIST_STORAGE_KEY, list);
  } catch {
    // ignore
  }
}

async function callAnyListService(
  service: 'get_items' | 'add_item' | 'check_item' | 'remove_item',
  body: Record<string, unknown>,
): Promise<Response> {
  return apiFetch('/api/ha', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ domain: 'anylist', service, ...body }),
  });
}

interface AnyListWidgetProps {
  className?: string;
}

export function AnyListWidget({ className }: AnyListWidgetProps = {}) {
  const [activeList, setActiveList] = useState<ListName>(() => readPersistedList());
  // items is the server-truth snapshot from the latest poll. Pending mutations
  // overlay it via the local `pending*` sets so the UI feels instant without
  // mutating the server snapshot directly.
  const [items, setItems] = useState<Record<ListName, string[]>>({
    Haggen: [],
    Costco: [],
    Amazon: [],
    Todo: [],
  });
  const [pendingHidden, setPendingHidden] = useState<Set<string>>(new Set());
  const [pendingAdded, setPendingAdded] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  // On-screen keyboard for the add field (the kiosk has no OS keyboard).
  const [kbOpen, setKbOpen] = useState(false);
  const addInputRef = useRef<HTMLInputElement>(null);
  // Lists that have completed at least one fetch. Until a list is in here we
  // show a loading state rather than the empty "Nothing on the list" message,
  // so switching tabs doesn't flash empty before the first poll resolves.
  const [loadedLists, setLoadedLists] = useState<Set<ListName>>(new Set());
  const alive = useRef(true);

  // Switch active list + persist + reset transient state so optimistic
  // overlays don't leak from one tab to the other.
  function selectList(next: ListName): void {
    if (next === activeList) return;
    setActiveList(next);
    setPendingHidden(new Set());
    setPendingAdded([]);
    setError(null);
    setKbOpen(false);
    writePersistedList(next);
  }

  // On-screen keyboard edits for the add field.
  const typeDraft = useCallback((ch: string) => setDraft((d) => d + ch), []);
  const backspaceDraft = useCallback(() => setDraft((d) => d.slice(0, -1)), []);

  const refreshList = useCallback(async (list: ListName): Promise<void> => {
    try {
      const res = await callAnyListService('get_items', { list });
      if (!alive.current) return;
      if (!res.ok) {
        setError(`AnyList ${res.status}`);
        return;
      }
      const data = (await res.json()) as GetItemsResponse;
      const nextItems = data.response?.service_response?.items ?? [];
      setItems((prev) => ({ ...prev, [list]: nextItems }));
      setPendingHidden((prev) => {
        const next = new Set(prev);
        for (const name of prev) {
          if (!nextItems.includes(name)) next.delete(name);
        }
        return next.size === prev.size ? prev : next;
      });
      setPendingAdded((prev) => prev.filter((name) => !nextItems.includes(name)));
      setLoadedLists((prev) => (prev.has(list) ? prev : new Set(prev).add(list)));
      setError(null);
    } catch (e) {
      if (alive.current) setError(e instanceof Error ? e.message : 'fetch failed');
    }
  }, []);

  // Poll the active list. Switching tabs cancels the previous poll loop
  // (cleanup) and starts a fresh one — keeps the network footprint at
  // exactly one in-flight request per tick.
  useEffect(() => {
    alive.current = true;
    void refreshList(activeList);
    const stop = pollWithWake(
      () => void refreshList(activeList),
      POLL_INTERVAL_MS,
    );
    return () => {
      alive.current = false;
      stop();
    };
  }, [activeList, refreshList]);

  async function addItem(name: string): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed) return;
    setDraft('');
    setPendingAdded((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
    setFlashName(trimmed); // scroll-to + highlight the new row
    try {
      const res = await callAnyListService('add_item', { list: activeList, name: trimmed });
      if (!res.ok) throw new Error(`add ${res.status}`);
      void refreshList(activeList);
    } catch (e) {
      setPendingAdded((prev) => prev.filter((n) => n !== trimmed));
      setError(e instanceof Error ? e.message : 'add failed');
    }
  }

  async function checkItem(name: string): Promise<void> {
    // Optimistically hide; AnyList hides completed items from get_items so
    // the next refresh confirms the disappearance.
    setPendingHidden((prev) => {
      const next = new Set(prev);
      next.add(name);
      return next;
    });
    try {
      const res = await callAnyListService('check_item', { list: activeList, name });
      if (!res.ok) throw new Error(`check ${res.status}`);
      void refreshList(activeList);
    } catch (e) {
      setPendingHidden((prev) => {
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
      setError(e instanceof Error ? e.message : 'check failed');
    }
  }

  async function deleteItem(name: string): Promise<void> {
    setPendingHidden((prev) => {
      const next = new Set(prev);
      next.add(name);
      return next;
    });
    try {
      const res = await callAnyListService('remove_item', {
        list: activeList,
        name,
        ignore_case: true,
      });
      if (!res.ok) throw new Error(`remove ${res.status}`);
      void refreshList(activeList);
    } catch (e) {
      setPendingHidden((prev) => {
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
      setError(e instanceof Error ? e.message : 'remove failed');
    }
  }

  // AnyList has no rename service, so an edit is remove(old) + add(new).
  // Optimistically hide the old name and surface the new one so the row
  // updates instantly; the next poll reconciles.
  async function renameItem(oldName: string, newName: string): Promise<void> {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName) return;
    setPendingHidden((prev) => new Set(prev).add(oldName));
    setPendingAdded((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
    try {
      const rm = await callAnyListService('remove_item', {
        list: activeList,
        name: oldName,
        ignore_case: true,
      });
      if (!rm.ok) throw new Error(`remove ${rm.status}`);
      const ad = await callAnyListService('add_item', { list: activeList, name: trimmed });
      if (!ad.ok) throw new Error(`add ${ad.status}`);
      void refreshList(activeList);
    } catch (e) {
      setPendingHidden((prev) => {
        const next = new Set(prev);
        next.delete(oldName);
        return next;
      });
      setPendingAdded((prev) => prev.filter((n) => n !== trimmed));
      setError(e instanceof Error ? e.message : 'rename failed');
    }
  }

  // Build the displayed list: server items not in pendingHidden, then
  // pendingAdded appended at the BOTTOM. AnyList adds new items to the end of
  // the list, so placing optimistic adds at the bottom too means they don't
  // jump from top → bottom when the server snapshot arrives.
  const displayed = [
    ...items[activeList].filter((name) => !pendingHidden.has(name)),
    ...pendingAdded.filter((name) => !items[activeList].includes(name)),
  ];

  // Soft, time-based top/bottom edge fades on the scroll area (shared hook).
  const scrollFade = useScrollFade([displayed]);

  // Name of the just-added item: scroll it into view at the bottom and flash
  // it for ~1.6s (the row-flash util). Keep our own ref to the scroll element
  // (merged with the fade hook's callback ref) so we can scrollTo it.
  const [flashName, setFlashName] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const setListRef = useCallback(
    (node: HTMLDivElement | null) => {
      listRef.current = node;
      scrollFade.ref(node);
    },
    [scrollFade.ref],
  );
  useEffect(() => {
    if (!flashName) return;
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
    const t = setTimeout(() => setFlashName(null), 1600);
    return () => clearTimeout(t);
  }, [flashName]);

  return (
    <div
      className={`card flex flex-col gap-3 h-full overflow-hidden ${className ?? ''}`}
    >
      {/* Header: tab strip + count */}
      <div className="flex items-center gap-3">
        <div className="flex gap-1 flex-1">
          {LISTS.map((list) => {
            const isActive = list === activeList;
            return (
              <button
                key={list}
                type="button"
                onClick={() => selectList(list)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-fg text-canvas'
                    : 'bg-overlay-dark text-fg-secondary hover:bg-elevated'
                }`}
              >
                {list}
              </button>
            );
          })}
        </div>
        <span className="text-xs text-fg-tertiary tabular shrink-0">
          {displayed.length} {displayed.length === 1 ? 'item' : 'items'}
        </span>
      </div>

      {/* Add item input — mirrors the MusicBrowserModal search field: a card
          wrapper with a leading icon, a transparent borderless input, and a
          trailing pill button. */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void addItem(draft);
        }}
        className="flex items-center gap-3 card !py-3 !bg-canvas"
      >
        <PlusIcon size={22} weight="bold" className="shrink-0 text-fg-secondary" />
        <input
          ref={addInputRef}
          type="text"
          // We provide our own on-screen keyboard; suppress any native one.
          inputMode="none"
          value={draft}
          onFocus={() => setKbOpen(true)}
          onBlur={() => setKbOpen(false)}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add to list…"
          className="flex-1 bg-transparent outline-none text-lg text-fg placeholder:text-fg-tertiary"
        />
        {/* Only present once there's something to add — an inert disabled
            button is just noise on a touch display. */}
        {draft.trim() && (
          <button
            type="submit"
            className="shrink-0 -my-1 h-7 px-4 inline-flex items-center rounded-full bg-elevated border border-border text-sm text-fg-secondary transition-transform active:scale-[0.97]"
          >
            Add
          </button>
        )}
      </form>

      {/* Items list (scrollable) */}
      <div
        ref={setListRef}
        onScroll={scrollFade.onScroll}
        className="flex-1 overflow-y-auto -mx-1 px-2"
        style={scrollFade.style}
      >
        {displayed.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-fg-tertiary">
            {error
              ? error
              : loadedLists.has(activeList)
                ? 'Nothing on the list'
                : 'Loading…'}
          </div>
        ) : (
          <ul className="flex flex-col [&>li:first-child>div]:border-t-0">
            {displayed.map((name) => (
              <ItemRow
                key={name}
                name={name}
                flash={name === flashName}
                onCheck={() => void checkItem(name)}
                onDelete={() => void deleteItem(name)}
                onRename={(newName) => void renameItem(name, newName)}
              />
            ))}
          </ul>
        )}
      </div>

      {kbOpen && (
        <KeyboardOverlay
          onKey={typeDraft}
          onBackspace={backspaceDraft}
          onEnter={() => void addItem(draft)}
          enterLabel="Add"
          onClose={() => {
            setKbOpen(false);
            addInputRef.current?.blur();
          }}
        />
      )}
    </div>
  );
}

interface ItemRowProps {
  name: string;
  flash?: boolean; // briefly highlight a just-added item
  onCheck: () => void;
  onDelete: () => void;
  onRename: (newName: string) => void;
}

// One shopping-list row: a checkbox (the only check target) + the item name
// + a pencil that opens the edit modal. The modal holds the name input plus
// Save (rename) and Delete actions. No swipe gestures.
function ItemRow({ name, flash, onCheck, onDelete, onRename }: ItemRowProps) {
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState(name);
  // On-screen keyboard for the rename field (no OS keyboard on the kiosk).
  const [kbOpen, setKbOpen] = useState(false);
  const editInputRef = useRef<HTMLInputElement>(null);

  function openEditor(): void {
    setEditDraft(name);
    setEditing(true);
  }

  function save(): void {
    setEditing(false);
    setKbOpen(false);
    onRename(editDraft);
  }

  function remove(): void {
    setEditing(false);
    setKbOpen(false);
    onDelete();
  }

  const typeEdit = (ch: string) => setEditDraft((d) => d + ch);
  const backspaceEdit = () => setEditDraft((d) => d.slice(0, -1));

  return (
    <li className={`flex items-center gap-3 rounded-lg${flash ? ' row-flash' : ''}`}>
      {/* Checkbox — the only spot that checks the item off. Sized large for an
          easy touch target. Sits outside the divider so the border between
          items starts at the label, not under the checkbox. */}
      <button
        type="button"
        onClick={onCheck}
        aria-label="Check off item"
        className="shrink-0 w-7 h-7 rounded-lg bg-elevated active:scale-90 transition-transform"
      />
      <div className="flex-1 flex items-center gap-3 py-2.5 border-t border-border text-fg">
        <span className="text-sm flex-1 truncate">{name}</span>
        <button
          type="button"
          onClick={openEditor}
          aria-label="Edit item"
          className="shrink-0 -my-1 p-1.5 rounded-md text-fg-tertiary hover:text-fg active:bg-elevated"
        >
          <PencilSimpleIcon size={16} weight="bold" />
        </button>
      </div>

      {editing && (
        <Modal
          onClose={() => {
            setEditing(false);
            setKbOpen(false);
          }}
          size="sm"
        >
          <div className="p-4 flex flex-col gap-3">
            <h2 className="text-sm font-semibold text-fg-secondary">Edit item</h2>
            <input
              ref={editInputRef}
              autoFocus
              // We provide our own on-screen keyboard; suppress any native one.
              inputMode="none"
              value={editDraft}
              onFocus={() => setKbOpen(true)}
              onBlur={() => setKbOpen(false)}
              onChange={(e) => setEditDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  save();
                }
              }}
              placeholder="Item name"
              className="input"
            />
          </div>
          {kbOpen && (
            <KeyboardOverlay
              onKey={typeEdit}
              onBackspace={backspaceEdit}
              onEnter={save}
              enterLabel="Save"
              onClose={() => {
                setKbOpen(false);
                editInputRef.current?.blur();
              }}
            />
          )}
          <ModalFooter align="between">
            <button type="button" onClick={remove} className="btn btn-danger-outline">
              <TrashIcon size={16} weight="bold" className="mr-1.5" />
              Delete
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!editDraft.trim()}
              className="btn btn-primary"
            >
              Save
            </button>
          </ModalFooter>
        </Modal>
      )}
    </li>
  );
}
